import MapProjectService from '../services/MapProjectService';
import MapLayerManager from '../services/MapLayerManager';
import MapLogger from '../utils/MapLogger';
import MapErrorHandler from '../utils/MapErrorHandler';

/**
 * Project Display Controller
 * Manages display of residential and land projects separately
 */
class ProjectDisplayController {
  constructor(mapRef, onParcelClick = null) {
    this.mapRef = mapRef;
    this.onParcelClick = onParcelClick;
    this.currentProject = null;
    this.currentProjectType = null;
    this.currentFloorFilters = [];
    this.isLoading = false;
    this.lastError = null;
  }

  /**
   * Display residential project
   * @param {number} projectId - Project ID
   * @param {Array} floorFilters - Array of floor IDs
   * @param {Object} project - Project data
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} - Display result
   */
  async displayResidentialProject(projectId, floorFilters, project, options = {}) {
    try {
      MapLogger.logOperationStart('display-residential-project', { 
        projectId, 
        floorFilters, 
        options 
      });

      // // Check if same project is already being displayed with same floor filters
      // if (this.currentProject === projectId && this.currentProjectType === 'residential' && 
      //     JSON.stringify(this.currentFloorFilters) === JSON.stringify(floorFilters) && !this.isLoading) {
      //   MapLogger.logMapInfo('Same residential project with same floor filters already displayed in controller, skipping', { 
      //     projectId, 
      //     floorFilters 
      //   });
      //   return { success: true, message: 'Project already displayed' };
      // }

      // Prevent multiple simultaneous loads
      if (this.isLoading) {
        MapLogger.logMapInfo('Project display already in progress in controller, skipping', { projectId });
        return { success: false, error: 'Display already in progress' };
      }

      this.isLoading = true;
      this.lastError = null;

      // Validate inputs
      if (!this.validateResidentialProjectInputs(projectId, floorFilters, project)) {
        throw new Error('Invalid inputs for residential project');
      }

      // Clear current project
      await this.clearCurrentProject();

      // Load and display residential project
      const result = await MapProjectService.loadResidentialProject(
        this.mapRef, 
        projectId, 
        floorFilters, 
        project,
        this.onParcelClick
      );

      MapLogger.logMapInfo('Residential project load result', { 
        projectId, 
        hasResult: !!result,
        resultSuccess: result ? result.success : 'N/A',
        resultError: result ? result.error : 'N/A',
        resultMessage: result ? result.message : 'N/A',
        resultKeys: result ? Object.keys(result) : []
      });

      if (result && result.success) {
        this.currentProject = projectId;
        this.currentProjectType = 'residential';
        this.currentFloorFilters = floorFilters;

        MapLogger.logOperationEnd('display-residential-project', true, { projectId });
        return { success: true, data: result.data };
      } else {
        const errorMessage = (result && result.error) ? result.error : 'Failed to load residential project';
        throw new Error(errorMessage);
      }

    } catch (error) {
      this.lastError = error;
      MapErrorHandler.handleProjectLoadError(error, projectId, { 
        projectType: 'residential',
        floorFilters 
      });
      MapLogger.logOperationEnd('display-residential-project', false, { 
        error: error.message 
      });
      return { success: false, error: error.message };
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Display land project
   * @param {number} projectId - Project ID
   * @param {Object} project - Project data
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} - Display result
   */
  async displayLandProject(projectId, project, options = {}) {
    try {
      MapLogger.logOperationStart('display-land-project', { 
        projectId, 
        options 
      });

      // Check if same project is already being displayed
      if (this.currentProject === projectId && this.currentProjectType === 'land' && !this.isLoading) {
        MapLogger.logMapInfo('Same land project already displayed in controller, skipping', { projectId });
        return { success: true, message: 'Project already displayed' };
      }

      // Prevent multiple simultaneous loads
      if (this.isLoading) {
        MapLogger.logMapInfo('Project display already in progress in controller, skipping', { projectId });
        return { success: false, error: 'Display already in progress' };
      }

      this.isLoading = true;
      this.lastError = null;

      // Validate inputs
      if (!this.validateLandProjectInputs(projectId, project)) {
        throw new Error('Invalid inputs for land project');
      }

      // Clear current project
      await this.clearCurrentProject();

      // Load and display land project
      const result = await MapProjectService.loadLandProject(
        this.mapRef, 
        projectId, 
        project,
        this.onParcelClick
      );

      if (result && result.success) {
        this.currentProject = projectId;
        this.currentProjectType = 'land';
        this.currentFloorFilters = [];

        MapLogger.logOperationEnd('display-land-project', true, { projectId });
        return { success: true, data: result.data };
      } else {
        const errorMessage = (result && result.error) ? result.error : 'Failed to load land project';
        throw new Error(errorMessage);
      }

    } catch (error) {
      this.lastError = error;
      MapErrorHandler.handleProjectLoadError(error, projectId, { 
        projectType: 'land' 
      });
      MapLogger.logOperationEnd('display-land-project', false, { 
        error: error.message 
      });
      return { success: false, error: error.message };
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Apply filters to current project
   * @param {Object} filters - Applied filters
   * @param {Array} currentFloorFilters - Current floor filters for residential projects
   * @returns {Promise<Object>} - Filter application result
   */
  async applyFilters(filters, currentFloorFilters = null) {
    try {
      MapLogger.logOperationStart('apply-filters', {
        projectId: this.currentProject,
        projectType: this.currentProjectType,
        filters,
        currentFloorFilters
      });

      if (!this.currentProject) {
        throw new Error('No project currently displayed');
      }

      // Apply filters using service, passing current floor filters for residential projects
      MapProjectService.applyFilters(this.mapRef, filters, this.currentProjectType, currentFloorFilters);

      MapLogger.logOperationEnd('apply-filters', true, {
        projectId: this.currentProject
      });
      return { success: true };

    } catch (error) {
      MapErrorHandler.handleFilterError(error, 'general', {
        projectId: this.currentProject,
        filters,
        currentFloorFilters
      });
      MapLogger.logOperationEnd('apply-filters', false, {
        error: error.message 
      });
      return { success: false, error: error.message };
    }
  }

  /**
   * Update floor filters for residential projects
   * @param {Array} newFloorFilters - New floor filters
   * @returns {Promise<Object>} - Update result
   */
  async updateFloorFilters(newFloorFilters) {
    try {
      MapLogger.logOperationStart('update-floor-filters', { 
        projectId: this.currentProject,
        oldFilters: this.currentFloorFilters,
        newFilters: newFloorFilters 
      });

      if (this.currentProjectType !== 'residential') {
        throw new Error('Floor filters can only be applied to residential projects');
      }

      if (!this.currentProject) {
        throw new Error('No project currently displayed');
      }

      // Update floor filters
      this.currentFloorFilters = newFloorFilters;

      // Reload project with new filters
      const result = await this.displayResidentialProject(
        this.currentProject, 
        newFloorFilters, 
        { id: this.currentProject }
      );

      MapLogger.logOperationEnd('update-floor-filters', true, { 
        projectId: this.currentProject 
      });
      return result;

    } catch (error) {
      MapErrorHandler.handleFilterError(error, 'floor-filters', { 
        projectId: this.currentProject,
        newFloorFilters 
      });
      MapLogger.logOperationEnd('update-floor-filters', false, { 
        error: error.message 
      });
      return { success: false, error: error.message };
    }
  }

  /**
   * Clear current project
   * @returns {Promise<Object>} - Clear result
   */
  async clearCurrentProject() {
    try {
      MapLogger.logOperationStart('clear-current-project', { 
        projectId: this.currentProject,
        projectType: this.currentProjectType 
      });

      // Clear layers and sources
      await MapLayerManager.removeLayers(this.mapRef);
      await MapLayerManager.removeSources(this.mapRef);

      // Reset state
      this.currentProject = null;
      this.currentProjectType = null;
      this.currentFloorFilters = [];
      this.lastError = null;

      MapLogger.logOperationEnd('clear-current-project', true);
      return { success: true };

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'clear-project');
      MapLogger.logOperationEnd('clear-current-project', false, { 
        error: error.message 
      });
      return { success: false, error: error.message };
    }
  }

  /**
   * Validate residential project inputs
   * @param {number} projectId - Project ID
   * @param {Array} floorFilters - Floor filters
   * @param {Object} project - Project data
   * @returns {boolean} - Input validity
   */
  validateResidentialProjectInputs(projectId, floorFilters, project) {
    if (!projectId || typeof projectId !== 'number') {
      MapLogger.logWarning('validation', 'Invalid project ID for residential project');
      return false;
    }

    if (!floorFilters || !Array.isArray(floorFilters) || floorFilters.length === 0) {
      MapLogger.logWarning('validation', 'Invalid floor filters for residential project');
      return false;
    }

    if (!project || typeof project !== 'object') {
      MapLogger.logWarning('validation', 'Invalid project data for residential project');
      return false;
    }

    return true;
  }

  /**
   * Validate land project inputs
   * @param {number} projectId - Project ID
   * @param {Object} project - Project data
   * @returns {boolean} - Input validity
   */
  validateLandProjectInputs(projectId, project) {
    if (!projectId || typeof projectId !== 'number') {
      MapLogger.logWarning('validation', 'Invalid project ID for land project');
      return false;
    }

    if (!project || typeof project !== 'object') {
      MapLogger.logWarning('validation', 'Invalid project data for land project');
      return false;
    }

    return true;
  }

  /**
   * Get current project information
   * @returns {Object} - Current project information
   */
  getCurrentProjectInfo() {
    return {
      projectId: this.currentProject,
      projectType: this.currentProjectType,
      floorFilters: this.currentFloorFilters,
      isLoading: this.isLoading,
      lastError: this.lastError
    };
  }

  /**
   * Check if project is displayed
   * @returns {boolean} - Project display status
   */
  hasCurrentProject() {
    return this.currentProject !== null;
  }

  /**
   * Check current project type
   * @param {string} projectType - Required project type
   * @returns {boolean} - Project type match
   */
  isCurrentProjectType(projectType) {
    return this.currentProjectType === projectType;
  }

  /**
   * Reload current project
   * @returns {Promise<Object>} - Reload result
   */
  async reloadCurrentProject() {
    try {
      if (!this.currentProject) {
        throw new Error('No project to reload');
      }

      MapLogger.logOperationStart('reload-current-project', { 
        projectId: this.currentProject,
        projectType: this.currentProjectType 
      });

      let result;

      if (this.currentProjectType === 'residential') {
        result = await this.displayResidentialProject(
          this.currentProject,
          this.currentFloorFilters,
          { id: this.currentProject }
        );
      } else if (this.currentProjectType === 'land') {
        result = await this.displayLandProject(
          this.currentProject,
          { id: this.currentProject }
        );
      } else {
        throw new Error('Unknown project type');
      }

      MapLogger.logOperationEnd('reload-current-project', result.success, { 
        projectId: this.currentProject 
      });
      return result;

    } catch (error) {
      MapErrorHandler.handleProjectLoadError(error, this.currentProject, { 
        operation: 'reload' 
      });
      MapLogger.logOperationEnd('reload-current-project', false, { 
        error: error.message 
      });
      return { success: false, error: error.message };
    }
  }

  /**
   * Update map reference
   * @param {Object} newMapRef - New map reference
   */
  updateMapRef(newMapRef) {
    this.mapRef = newMapRef;
    MapLogger.logMapInfo('Map reference updated');
  }

  /**
   * Reset controller
   */
  reset() {
    this.currentProject = null;
    this.currentProjectType = null;
    this.currentFloorFilters = [];
    this.isLoading = false;
    this.lastError = null;
    MapLogger.logMapInfo('Controller reset');
  }
}

export default ProjectDisplayController;
